import { IDENTITY, invert, multiply, type Matrix } from '../geometry/matrix';
import {
  segmentsBounds,
  serializeSegments,
  transformSegments,
  type Segment,
} from '../geometry/path';
import { inflateRect, unionRects, type Rect } from '../geometry/rect';
import { nailSilhouetteSegments } from '../nails/shapes';
import { findDesign } from '../model/tree';
import type { Design, NailInstance, ProjectDocument, VectorNode } from '../model/types';
import { resolveFill, type ResolvedFill } from './fill';
import { parsePathCached } from './parseCache';
import { resolveStroke, strokeOutset, type ResolvedStroke } from './stroke';

/**
 * Pure scene evaluation (docs/08): the single definition of what a design looks like.
 * Rendering, bounds, hit-testing, thumbnails, previews, and SVG/PDF export all read the
 * world-space paths produced here, so they cannot drift apart (docs/05).
 */
export interface EvaluatedPath {
  /** The path node that produced this geometry. */
  readonly nodeId: string;
  /** Top-level node of the design that contains the path (the default selection unit). */
  readonly rootId: string;
  /** IDs from the root node down to the path, inclusive. */
  readonly nodePath: readonly string[];
  /** World-space segments (design millimeters, or nail millimeters when placed). */
  readonly segments: readonly Segment[];
  /** Serialized world-space path data. */
  readonly d: string;
  /** Geometric bounds without stroke width, or null for a path that draws nothing. */
  readonly bounds: Rect | null;
  /** Paint inside the shape, or null for line art. */
  readonly fill: ResolvedFill | null;
  /** The line along the outline, or null for a filled shape without one. */
  readonly stroke: ResolvedStroke | null;
}

export interface EvaluatedScene {
  readonly paths: readonly EvaluatedPath[];
  /** Geometric bounds of all visible paths. */
  readonly bounds: Rect | null;
  /** Bounds including stroke ink reach (docs/07 layout uses physical visible bounds). */
  readonly visualBounds: Rect | null;
}

export interface EvaluateOptions {
  /** Transform applied to the whole design after node transforms (placement, output scale). */
  readonly outer?: Matrix;
  /**
   * Transient design-space delta applied to the selected nodes (at any depth) during a
   * gesture. Editor-only preview; it must equal what the committed command produces.
   */
  readonly transient?: { readonly nodeIds: ReadonlySet<string>; readonly delta: Matrix };
  /** Serialization precision for `d` in millimeters (4 decimals = 0.1 µm). */
  readonly precision?: number;
}

export function evaluateDesign(design: Design, options: EvaluateOptions = {}): EvaluatedScene {
  return evaluateNodes(design.nodes, options);
}

export function evaluateNodes(
  nodes: readonly VectorNode[],
  options: EvaluateOptions = {},
): EvaluatedScene {
  const outer = options.outer ?? IDENTITY;
  const precision = options.precision ?? 4;
  const paths: EvaluatedPath[] = [];

  const transient = options.transient;
  // The delta acts in design space (world' = delta × world) before `outer` maps into nail
  // or page space: conjugate it into output space once.
  const outerInverse = transient ? invert(outer) : null;
  const transientInOutput =
    transient && outerInverse ? multiply(multiply(outer, transient.delta), outerInverse) : null;

  const visit = (
    node: VectorNode,
    parentWorld: Matrix,
    strokeOuter: Matrix,
    rootId: string,
    trail: readonly string[],
  ) => {
    if (!node.visible) return; // hidden nodes stay in the document but never render or print
    let world = multiply(parentWorld, node.transform);
    let nodeStrokeOuter = strokeOuter;
    if (transientInOutput && transient!.nodeIds.has(node.id)) {
      world = multiply(transientInOutput, world);
      // Previews scale-with-design strokes exactly as the committed command will.
      nodeStrokeOuter = multiply(strokeOuter, transient!.delta);
    }
    const nodePath = [...trail, node.id];
    if (node.type === 'group') {
      for (const child of node.children) visit(child, world, nodeStrokeOuter, rootId, nodePath);
      return;
    }
    const segments = transformSegments(parsePathCached(node.d), world);
    paths.push({
      nodeId: node.id,
      rootId,
      nodePath,
      segments,
      d: serializeSegments(segments, precision),
      bounds: segmentsBounds(segments),
      fill: resolveFill(node.fill),
      stroke: node.stroke === 'none' ? null : resolveStroke(node.stroke, nodeStrokeOuter),
    });
  };

  for (const root of nodes) visit(root, outer, outer, root.id, []);
  return summarize(paths);
}

export function summarize(paths: readonly EvaluatedPath[]): EvaluatedScene {
  const bounds = unionRects(paths.map((p) => p.bounds));
  const visualBounds = unionRects(
    paths.map((p) =>
      p.bounds ? inflateRect(p.bounds, p.stroke ? strokeOutset(p.stroke) : 0) : null,
    ),
  );
  return { paths, bounds, visualBounds };
}

export interface EvaluatedNail {
  readonly nail: NailInstance;
  readonly design: Design;
  /** Silhouette in nail millimeters; bounds are exactly [0, 0, widthMm, lengthMm]. */
  readonly silhouette: readonly Segment[];
  readonly silhouetteD: string;
  /** Design art mapped through the nail placement into nail millimeters (unclipped). */
  readonly scene: EvaluatedScene;
}

/**
 * Evaluates a nail: design node transforms first, then the nail's placement matrix;
 * the silhouette is the clip. Display-only transforms (hand layout, camera) are
 * applied by preview code afterwards and never written back (docs/05).
 */
export function evaluateNail(
  doc: ProjectDocument,
  nail: NailInstance,
  options: { precision?: number; outputScale?: Matrix } = {},
): EvaluatedNail | null {
  const design = findDesign(doc, nail.designId);
  if (!design) return null;
  const precision = options.precision ?? 4;
  const outer = options.outputScale
    ? multiply(options.outputScale, nail.placement)
    : nail.placement;
  const silhouetteLocal = nailSilhouetteSegments(nail.shape, nail.widthMm, nail.lengthMm);
  const silhouette = options.outputScale
    ? transformSegments(silhouetteLocal, options.outputScale)
    : silhouetteLocal;
  return {
    nail,
    design,
    silhouette,
    silhouetteD: serializeSegments(silhouette, precision),
    scene: evaluateDesign(design, { outer, precision }),
  };
}
